import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Select,
  Notification,
} from '@arco-design/web-react'
import { FormInstance } from '@arco-design/web-react/es/Form'
import {
  IconLanguage,
  IconLock,
  IconUser,
  IconArrowRight,
} from '@arco-design/web-react/icon'
import axios from 'axios'
import useStorage from '@/hooks/useStorage'
import useLocale from '@/hooks/useLocale'
import locale from './locale'
import styles from './style/index.module.less'
import { inviteRegisterUrl, loginUrl } from '@/apis/account'
import {
  EMAIL_REGEX,
  getLoginIdentifierValidationError,
  PHONE_REGEX,
} from './validators'
import IconButton from '@/components/NavBar/IconButton'
import { GlobalContext } from '@/context'
import { Grid } from '@arco-design/web-react'
import { baseUrl, getSetting } from '@/utils/settings'
import { useHistory, useLocation } from 'react-router-dom'
import {
  appendServiceCodeToPath,
  appendServiceCodeToRequestUrl,
  getServiceCode,
} from '@/utils/serviceCode'
const Row = Grid.Row
const Col = Grid.Col
const KEY_TOKEN_LOGIN = getSetting('KEY_TOKEN_LOGIN')
const KEY_USER_INFO = getSetting('KEY_USER_INFO')

export default function LoginForm() {
  const formRef = useRef<FormInstance | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [loginPrefill, setLoginPrefill] = useState<{
    userName?: string
    password?: string
  } | null>(null)
  const history = useHistory()
  const location = useLocation()
  const [loginParams, setLoginParams, removeLoginParams] =
    useStorage('loginParams')
  const [, setUserInfo] = useStorage(KEY_USER_INFO)
  const [, setToken] = useStorage(KEY_TOKEN_LOGIN)
  const { setLang, lang } = useContext(GlobalContext)
  const t = useLocale(locale)
  const rememberPassword = !!loginParams
  const isRegisterMode = useMemo(() => {
    return /^\/register\/?$/.test(location.pathname)
  }, [location.pathname])
  const companyFromUrl = useMemo(() => {
    return new URLSearchParams(location.search).get('company') || ''
  }, [location.search])
  const inviteCodeFromUrl = useMemo(() => {
    return new URLSearchParams(location.search).get('inviteCode') || ''
  }, [location.search])
  const subTitle = companyFromUrl
    ? t['login.form.inviteRegisterSubtitle'].replace('{company}', companyFromUrl)
    : `${t['login.form.subtitle']} - ${t['login.systemName']}`

  useEffect(() => {
    if (isRegisterMode && inviteCodeFromUrl) {
      formRef.current?.setFieldValue('inviteCode', inviteCodeFromUrl)
    }
  }, [inviteCodeFromUrl, isRegisterMode])

  useEffect(() => {
    if (isRegisterMode || loginPrefill === null) return
    formRef.current?.resetFields()
    if (loginPrefill.userName) {
      formRef.current?.setFieldValue('userName', loginPrefill.userName)
    }
    if (loginPrefill.password) {
      formRef.current?.setFieldValue('password', loginPrefill.password)
    }
    setLoginPrefill(null)
  }, [isRegisterMode, loginPrefill])

  useEffect(() => {
    const touchedFields = formRef.current?.getTouchedFields?.() || []
    if (!touchedFields.length) return
    formRef.current?.validate?.(touchedFields as any).catch(() => {
      console.log('validate error', touchedFields)
    })
  }, [lang])

  function afterLoginSuccess(params) {
    // 记住密码
    if (rememberPassword) {
      setLoginParams(JSON.stringify(params))
    } else {
      removeLoginParams()
    }
    // 已登录后，若当前在登录/注册路由，回到首页；否则保留原目标路由
    const isAuthRoute = /^\/(login|register)\/?$/.test(location.pathname)
    const targetPath = isAuthRoute
      ? '/'
      : `${location.pathname}${location.search}${location.hash}`
    window.location.href = appendServiceCodeToPath(targetPath)
  }

  function getAuthRequestUrl(path: string) {
    const apiRoot = (baseUrl() || '').trim().replace(/\/$/, '')
    if (!apiRoot) return path
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${apiRoot}${normalizedPath}`
  }

  function login(params) {
    const loginIdentifier = `${params?.userName || ''}`.trim()
    const loginIdentifierError =
      getLoginIdentifierValidationError(loginIdentifier)

    if (loginIdentifierError) {
      const content =
        loginIdentifierError === 'empty'
          ? t['login.form.loginAccount.errMsg']
          : t['login.form.loginAccount.invalid']
      setErrorMessage(content)
      Notification.error({
        title: t['login.msg.title'],
        content,
        showIcon: true,
        position: 'bottomRight',
        duration: 1500,
      })
      return
    }

    setErrorMessage('')
    setLoading(true)
    const serviceCode = getServiceCode()
    const loginRequestUrl = appendServiceCodeToRequestUrl(
      getAuthRequestUrl(loginUrl())
    )
    axios
      .post(loginRequestUrl, {
        username: loginIdentifier,
        password: params.password,
        serviceCode,
        userKey:''
      })
      .then((response) => {
        console.log(response, JSON.stringify(response.data), 'login')
        if (response.status === 200) {
          if (response.data?.result?.succeeded === true) {
            const res = {
              ...response.data,
            }
            setUserInfo(JSON.stringify(res))
            setToken(res?.bcToken || res?.token || res.ucToken || '')
            afterLoginSuccess(params)
          } else {
            let content = t['login.msg.userOrPwdError']
            if (response.data?.result?.isNotAllowed) {
              setErrorMessage(t['login.msg.isNotAllowed'])
              content = t['login.msg.isNotAllowed']
            } else {
              setErrorMessage(t['login.msg.userOrPwdError'])
              content = t['login.msg.userOrPwdError']
            }
            Notification.error({
              title: t['login.msg.title'],
              content: content,
              showIcon: true,
              position: 'bottomRight',
              duration: 1500,
            })
          }
        } else {
          setErrorMessage(t['login.msg.userOrPwdError'])
        }
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data?.error?.message??t['login.form.login.errMsg'])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function register(params) {
    setErrorMessage('')
    setLoading(true)
    const serviceCode = getServiceCode()
    const registerRequestUrl = appendServiceCodeToRequestUrl(
      getAuthRequestUrl(inviteRegisterUrl())
    )
    axios
      .post(registerRequestUrl, {
        email: params.email,
        name: params.name || '',
        userName: params.phoneNumber,
        phoneNumber: params.phoneNumber,
        password: params.password,
        invitationCode: params.inviteCode || '',
        serviceCode,
      })
      .then((response) => {
        if (
          response.status === 200 ||
           response.status === 204
        ) {
          Notification.success({
            title: t['login.msg.registerTitle'],
            content: t['login.msg.registerSuccess'],
            showIcon: true,
            position: 'bottomRight',
            duration: 1500,
          })
          setLoginPrefill({
            userName: params.phoneNumber,
            password: params.password,
          })
          gotoMode(false)
        } else {
          const content =
            response.data?.error?.message || t['login.msg.registerFailed']
          setErrorMessage(content)
          Notification.error({
            title: t['login.msg.registerTitle'],
            content,
            showIcon: true,
            position: 'bottomRight',
            duration: 2000,
          })
        }
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data?.error?.message||t['login.msg.registerFailed'])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function gotoMode(registerMode: boolean) {
    setErrorMessage('')
    if (registerMode) {
      const searchParams = new URLSearchParams()
      if (companyFromUrl) {
        searchParams.set('company', companyFromUrl)
      }
      if (inviteCodeFromUrl) {
        searchParams.set('inviteCode', inviteCodeFromUrl)
      }
      const queryString = searchParams.toString()
      const query = queryString ? `?${queryString}` : ''
      history.push(`/register${query}`)
      return
    }
    history.push('/login')
  }

  function onSubmitClick() {
    formRef.current?.validate().then((values) => {
      if (isRegisterMode) {
        if (values.confirmPassword !== values.password) {
          const content = t['login.form.confirmPassword.notMatch']
          setErrorMessage(content)
          Notification.error({
            title: t['login.msg.registerTitle'],
            content,
            showIcon: true,
            position: 'bottomRight',
            duration: 1500,
          })
          return
        }
        register(values)
        return
      }
      login(values)
    })
  }

  return (
    <div
      className={`${styles['login-form-wrapper']} ${
        isRegisterMode ? styles['login-form-wrapper-register'] : ''
      }`}
    >
      <div className={styles['login-form-title']}>
        <Row>
          <Col flex="auto">
            {isRegisterMode
              ? t['login.form.registerTitle']
              : t['login.form.title']}
          </Col>
          <Col flex="50px">
            <Select
              triggerElement={<IconButton icon={<IconLanguage />} />}
              options={[
                { label: t['login.lang.zh'], value: 'zh-CN' },
                { label: t['login.lang.en'], value: 'en-US' },
              ]}
              value={lang}
              triggerProps={{
                autoAlignPopupWidth: false,
                autoAlignPopupMinWidth: true,
                position: 'br',
              }}
              trigger="hover"
              onChange={(value) => {
                setLang(value)
              }}
            />
          </Col>
        </Row>
      </div>
      <div className={styles['login-form-sub-title']}>{subTitle}</div>
      <div className={styles['login-form-error-msg']}>{errorMessage}</div>
      <Form
        className={styles['login-form']}
        layout="vertical"
        ref={formRef}
        initialValues={{
          name: '',
          email: '',
          userName: '',
          phoneNumber: '',
          password: '',
          confirmPassword: '',
          inviteCode: inviteCodeFromUrl,
        }}
      >
        {isRegisterMode ? (
          <div className={styles['register-grid']}>
            <Form.Item
              field="name"
              label={t['login.form.name.label']}
              rules={[
                { required: true, message: t['login.form.name.errMsg'] },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.name.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="inviteCode"
              label={t['login.form.inviteCode.label']}
              validateTrigger="onBlur"
              rules={[
                { required: true, message: t['login.form.inviteCode.errMsg'] },
              ]}
            >
              <Input
                placeholder={t['login.form.inviteCode.placeholder']}
                disabled
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="phoneNumber"
              label={t['login.form.phoneNumber.label']}
              rules={[
                { required: true, message: t['login.form.phoneNumber.errMsg'] },
                {
                  match: PHONE_REGEX,
                  message: t['login.form.phoneNumber.invalid'],
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.phoneNumber.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="email"
              label={t['login.form.email.label']}
              rules={[
                { required: true, message: t['login.form.email.errMsg'] },
                {
                  match: EMAIL_REGEX,
                  message: t['login.form.email.invalid'],
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.email.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="password"
              label={t['login.form.password.label']}
              rules={[
                { required: true, message: t['login.form.password.errMsg'] },
                {
                  validator: (value, callback) => {
                    const password = String(value || '')
                    if (password.length < 6) {
                      callback(t['login.form.password.length'])
                      return
                    }
                    callback()
                  },
                },
              ]}
            >
              <Input.Password
                prefix={<IconLock style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.password.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="confirmPassword"
              label={t['login.form.confirmPassword.label']}
              rules={[
                {
                  required: true,
                  message: t['login.form.confirmPassword.errMsg'],
                },
              ]}
            >
              <Input.Password
                prefix={<IconLock style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.confirmPassword.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
          </div>
        ) : (
          <>
            <Form.Item
              field="userName"
              label={t['login.form.loginAccount.label']}
              rules={[
                {
                  required: true,
                  message: t['login.form.loginAccount.errMsg'],
                },
                {
                  validator(value, callback) {
                    const validationError =
                      getLoginIdentifierValidationError(value)

                    if (validationError === 'empty') {
                      callback()
                      return
                    }

                    if (validationError === 'invalid') {
                      callback(t['login.form.loginAccount.invalid'])
                      return
                    }

                    callback()
                  },
                },
              ]}
            >
              <Input
                prefix={<IconUser style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.loginAccount.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
            <Form.Item
              field="password"
              label={t['login.form.password.label']}
              rules={[
                { required: true, message: t['login.form.password.errMsg'] },
              ]}
            >
              <Input.Password
                prefix={<IconLock style={{ paddingRight: '3px' }} />}
                placeholder={t['login.form.password.placeholder']}
                onPressEnter={onSubmitClick}
              />
            </Form.Item>
          </>
        )}
        {!isRegisterMode && (
          <div className={styles['login-form-forget-password']}>
            {/* {t['login.form.forgetPassword']} */}
          </div>
        )}
        <Button type="primary" long onClick={onSubmitClick} loading={loading}>
          {isRegisterMode
            ? t['login.form.registerSubmit']
            : t['login.form.login']}
          <IconArrowRight style={{ marginLeft: 8 }} />
        </Button>
        <div className={styles['login-form-switch-row']}>
          <span>
            {isRegisterMode
              ? t['login.form.switch.toLoginPrefix']
              : t['login.form.switch.toRegisterPrefix']}
          </span>
          <Button
            type="text"
            className={styles['login-form-switch-btn']}
            onClick={() => gotoMode(!isRegisterMode)}
          >
            {isRegisterMode
              ? t['login.form.switch.toLoginAction']
              : t['login.form.switch.toRegisterAction']}
          </Button>
        </div>
      </Form>
    </div>
  )
}
